/**
 * Dedupe-aware query invalidation for the chart-tab write surfaces.
 *
 * **Problem** (Andrew DevTools 2026-05-20): every chart mutation
 * (Add/Edit/Resolve/Delete on Allergies, Problems, Procedures, …)
 * fires the list GET TWICE — the first cancelled, the second
 * succeeds. The pattern:
 *
 *   POST CreateProcedure (201)
 *   GET  GetProcedureByPatientId (cancelled)
 *   GET  GetProcedureByPatientId (200)
 *
 * Two invalidations race for the same key:
 * 1. Mutation `onSuccess` calls `qc.invalidateQueries(...)`.
 * 2. The backend broadcasts `UpdatePatientChat` over SignalR. The
 *    SAME session receives the echo of its own action and tries to
 *    invalidate again. Whichever invalidation arrives SECOND cancels
 *    the first in-flight refetch and starts a new one.
 *
 * Either order is possible — SignalR (websocket) can outrun the
 * HTTP mutation response under some load conditions, or vice-versa.
 *
 * **Fix** (this helper): route BOTH invalidation sites (mutation
 * onSuccess + SignalR echo) through `invalidateIfNotInFlight()`.
 * The first arrival triggers the refetch; the second sees an
 * in-flight fetch and skips. The fetch that completes serves both
 * call-sites.
 *
 * The SignalR target is **cross-user / cross-tab sync** — when
 * ANOTHER user edits the same patient. That path is preserved:
 * other tabs / sessions have no local mutation in flight, so the
 * SignalR-driven invalidation runs normally and keeps them in sync.
 *
 * `isFetching` does prefix-match — broad-key invalidations
 * (`['patient-conditions', pid]` over `[..., 'Active']` +
 * `[..., 'Resolved']` partitions) are deduped correctly.
 */

import type {
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

/**
 * Two call shapes:
 *   1. `invalidateIfNotInFlight(qc, ['key', id])` — queryKey prefix
 *      match (most common — chart-write mutations).
 *   2. `invalidateIfNotInFlight(qc, { predicate })` — for filters
 *      that need predicate matching (e.g. macros — multiple cache
 *      slots per practitioner keyed by search query).
 *
 * The helper uses `isFetching(filters)` with the SAME filter object
 * to detect in-flight fetches, then dispatches `invalidateQueries`
 * with that filter when nothing is racing.
 */
export function invalidateIfNotInFlight(
  queryClient: QueryClient,
  filter: QueryKey | InvalidateQueryFilters,
): void {
  // Normalize the queryKey-shorthand form into the filter object the
  // React Query API expects. `Array.isArray` narrows to a tuple-style
  // QueryKey here; wrap into the filter shape.
  const filters: InvalidateQueryFilters = Array.isArray(filter)
    ? { queryKey: filter as QueryKey }
    : (filter as InvalidateQueryFilters);
  if (queryClient.isFetching(filters) > 0) {
    // Some matching fetch is already running — either the local
    // mutation's refetch or a parallel SignalR-driven refetch. The
    // in-flight one will deliver the same data this invalidation
    // would have triggered. Skip.
    return;
  }
  void queryClient.invalidateQueries(filters);
}
